import { API, Competition, CompetitionAPI, Endpoints, Profile, ProjectJuryVote } from "@brg/api";
import {
  COMPETITION_LONGLIST_GET_LIST_PARAM,
  COMPETITION_LONGLIST_VOTE_CRITERIA_ID,
  normalizeJuryVoteValue,
} from "_competition/const";
import { RequestFilterT } from "shared/contexts/VoteContext";

const preserveClassicJuryVoteValue = (value: unknown): number => {
  if (value === -1 || value == null) {
    return -1;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : -1;
  }
  return -1;
};

const normalizeProjectJuryVotes = (rows: ProjectJuryVote[] | undefined): ProjectJuryVote[] =>
  (rows ?? []).map((row) => ({
    ...row,
    voteValue: row?.voteCriteriaId === COMPETITION_LONGLIST_VOTE_CRITERIA_ID
      ? normalizeJuryVoteValue(row?.voteValue)
      : preserveClassicJuryVoteValue(row?.voteValue),
  }));

export type FetchDataT = {
  competitionId: string;
  projectId: string;
  lang: string;
  profile?: Profile;
  competitionIds?: string[];
  competitionStatus?: number[];
  cmptlnglst?: boolean;
  dispatch?: any;
  callback?: (val: any) => void;
}

const getNextJuryProjectRequest = (
  competitionId: string,
  currentProjectId: string,
  cmptlnglst?: boolean,
) => {
  let request = API.request()
    .endpoint(Endpoints.competitions)
    .url(`/api/Competition/GetNextJuryProject`)
    .get()
    .addQuery('competitionId', competitionId)
    .addQuery('currentProjectId', currentProjectId);

  if (cmptlnglst) {
    request = request.addQuery(COMPETITION_LONGLIST_GET_LIST_PARAM, true);
  }

  return request;
};

export const FETCH_VOTE_DATA_LIST_REQUEST = "FETCH_VOTE_DATA_LIST_REQUEST";
export const FETCH_VOTE_DATA_LIST_SUCCESS = "FETCH_VOTE_DATA_LIST_SUCCESS";
export const FETCH_VOTE_DATA_LIST_FAILURE = "FETCH_VOTE_DATA_LIST_FAILURE";
export const RESET_FETCH_VOTE_DATA_LIST = "RESET_FETCH_VOTE_DATA_LIST";

export const UPDATE_VOTE_REQUEST = "UPDATE_VOTE_REQUEST";
export const UPDATE_VOTE_SUCCESS = "UPDATE_VOTE_SUCCESS";
export const UPDATE_VOTE_FAILURE = "UPDATE_VOTE_FAILURE";
export const RESET_UPDATE_VOTE = 'RESET_UPDATE_VOTE';

export const FETCH_COMPETITIONS_REQUEST = "FETCH_COMPETITIONS_REQUEST";
export const FETCH_COMPETITIONS_SUCCESS = "FETCH_COMPETITIONS_SUCCESS";
export const FETCH_COMPETITIONS_FAILURE = "FETCH_COMPETITIONS_FAILURE";
export const RESET_COMPETITIONS = 'RESET_COMPETITIONS';

export const GET_NEXT_REQUEST = 'GET_NEXT_REQUEST';
export const GET_NEXT_SUCCESS = 'GET_NEXT_SUCCESS';
export const GET_NEXT_FAILURE = 'GET_NEXT_FAILURE';

export const GET_NEXT_DATA_REQUEST = 'GET_NEXT_DATA_REQUEST';
export const GET_NEXT_DATA_SUCCESS = 'GET_NEXT_DATA_SUCCESS';
export const GET_NEXT_DATA_FAILURE = 'GET_NEXT_DATA_FAILURE';

export const UPDATE_REQUEST_FILTER = 'UPDATE_REQUEST_FILTER';

export const SET_CURRENT_VOTE = 'SET_CURRENT_VOTE';

export const SET_PREV_CRITERIA = 'SET_PREV_CRITERIA';

export const RESET_VOTE_DATA = 'RESET_VOTE_DATA';

export const UPDATE_VOTE_DATA = 'UPDATE_VOTE_DATA';

export const VOICE_SET = 'VOICE_SET';

export const VOTE_END = 'VOTE_END';

export const requestVoteDataList = () => {
  return {
    type: FETCH_VOTE_DATA_LIST_REQUEST,
    payload: {
      isLoading: true,
      errorMessage: "",
    },
  };
}

export const receiveVoteDataList= ({ data, nextId }: any) => {
  return {
    type: FETCH_VOTE_DATA_LIST_SUCCESS,
    payload: {
      isLoading: false,
      criteriaList: data || [],
      hasData: !!data?.length,
      ...(typeof nextId === 'string' && { nextCriteriaId: nextId })
    },
  };
}

export const errorVoteDataList = (message: string) => {
  return {
    type: FETCH_VOTE_DATA_LIST_FAILURE,
    payload: {
      isLoading: false,
      errorMessage: message,
    },
  };
}

/****************************** */

export const requestCompetitions = () => {
  return {
    type: FETCH_COMPETITIONS_REQUEST,
    payload: {
      isLoading: true,
      errorMessage: "",
    },
  };
}

export const receiveCompetitions= (data: any) => {
  return {
    type: FETCH_COMPETITIONS_SUCCESS,
    payload: {
      isLoading: false,
      competitions: data
    },
  };
}

export const errorCompetitions = (message: string) => {
  return {
    type: FETCH_COMPETITIONS_FAILURE,
    payload: {
      isLoading: false,
      errorMessage: message,
    },
  };
}

/****************************** */

export const requestUpdateVote = () => {
  return {
    type: UPDATE_VOTE_REQUEST,
    payload: {
      isLoading: true,
      errorMessage: "",
    },
  };
}

export const receiveUpdateVote= (data: any) => {
  return {
    type: UPDATE_VOTE_SUCCESS,
    payload: {
      isLoading: false,
      currentList: [...data],
    },
  };
}

export const errorUpdateVote = (message: string) => {
  return {
    type: UPDATE_VOTE_FAILURE,
    payload: {
      isLoading: false,
      errorMessage: message,
    },
  };
}

export const resetUpdateVote= () => {
  return {
    type: RESET_UPDATE_VOTE,
    payload: {
      isLoading: false,
      errorMessage: "",
      voiceSet: true
    },
  };
}

/****************************** */

export const requestGetNext = () => {
  return {
    type: GET_NEXT_REQUEST,
    payload: {
      isLoading: true,
      isNextLoading: true,
      errorMessage: "",
    },
  };
}

export const receiveGetNext= (data: any) => {
  return {
    type: GET_NEXT_SUCCESS,
    payload: {
      nextCriteriaId: data,
      isLoading: false,
      isNextLoading: false
    }
  };
}

export const errorGetNext = (message: string) => {
  return {
    type: GET_NEXT_FAILURE,
    payload: {
      isLoading: false,
      isNextLoading: false,
      errorMessage: message,
    },
  };
}

/*************************/

export const requestGetNextData = () => {
  return {
    type: GET_NEXT_DATA_REQUEST,
    payload: {
      isLoading: true,
      isNextLoading: true,
      errorMessage: "",
    },
  };
}

export const receiveGetNextData= (data: any) => {
  return {
    type: GET_NEXT_DATA_SUCCESS,
    payload: {
      isLoading: false,
      nextCriteriaList: data
    }
  };
}

export const errorGetNextData = (message: string) => {
  return {
    type: GET_NEXT_DATA_FAILURE,
    payload: {
      isLoading: false,
      errorMessage: message,
    },
  };
}

/****************************** */

export const setVoteStatus = (flag: string) => {
  return {
    type: VOTE_END,
    payload: {
      voteEnd: flag
    }
  };
}

export const updateRequestFilter= (data: RequestFilterT) => {
  return {
    type: UPDATE_REQUEST_FILTER,
    payload: data
  };
}

export const updateCurrentVote = (data: Record<string,any>[]) => {
  return {
    type: SET_CURRENT_VOTE,
    payload: {
      ...data,
      voiceSet: false
    }
  };
}

export const setPrevCrt= (id: string, prevCrt?: any) => {
  return {
    type: SET_PREV_CRITERIA,
    payload: {
      prevCriteriaId: id,
      ...(prevCrt && { prevCriteriaList: prevCrt })
    }
  };
}

export const resetVoteData = () => {
  return {
    type: RESET_VOTE_DATA,
    payload: {}
  }
}

export const updateVoteData = () => {
  return {
    type: UPDATE_VOTE_DATA,
    payload: {}
  }
}

export const resetVoteDataList = () => {
  return {
    type: RESET_FETCH_VOTE_DATA_LIST,
    payload: {
      isLoading: false,
      // criteriaList: [],
      currentList: [],
      errorMessage: "",
    },
  };
}

export const fetchNextVote = async ({ 
  competitionId, 
  projectId, 
  lang,
  dispatch, 
  callback 
}: FetchDataT) => {
  dispatch(requestGetNextData());

    try {
      await CompetitionAPI.GetProjectJuryVotes({ competitionId, projectId })
      .then((v) => v?.json())
      .then((v) => API.toLang<ProjectJuryVote[]>(v, lang))
      .then((v) => {
        //@ts-ignore
        dispatch(receiveGetNextData(v.reduce((acc, a) => ([...acc, {...a }]), [])));
        callback && callback(true);
      })
    } 
    catch(err) {
      dispatch(errorGetNextData(String(err)));
    }
}

export const fetchNextId = ({ 
  competitionId, 
  projectId,
  cmptlnglst,
  lang, 
  dispatch, 
  callback,
}: FetchDataT, updateVotedStatus?: () => void) => {
  if (competitionId && projectId) {
    dispatch(requestGetNext())
    try {
      getNextJuryProjectRequest(competitionId, projectId, cmptlnglst)
      .send()
      .then((r) => {
        if (r?.status === 200) {
          return r?.json()
        }
        return r;
      })
      .then((id) => {
        callback && callback(true)
        if (typeof id === 'string') {
          dispatch(receiveGetNext(id))
          dispatch(setPrevCrt(projectId))
          return;
        } 
        else {
          dispatch(receiveGetNext(''))
          updateVotedStatus && updateVotedStatus();
        }
      })
    }
    catch (err) {
      dispatch(errorGetNext(String(err)))
    }

  }
}

export const fetchUpdateVote = async (data: any[], dispatch: any) => {

  dispatch(requestUpdateVote());
  
  try {
    await CompetitionAPI.UpdateJuryVotes(data)
    .then((r) => {
        dispatch(receiveUpdateVote(data));
        return;
      })
  }
  catch (err) {
    dispatch(errorUpdateVote(String(err)));
  }
}


export const fetchVotes = async ({ 
  competitionId, 
  projectId,
  cmptlnglst,
  lang, 
  dispatch, 
  callback 
}: FetchDataT) => {

  if (!(competitionId && projectId)) return null;

  dispatch(requestVoteDataList());

    try {
      await CompetitionAPI.GetProjectJuryVotes({ competitionId, projectId })
      .then((v) => v?.json())
      .then((v) => API.toLang<ProjectJuryVote[]>(v, lang))
      .then((v) => normalizeProjectJuryVotes(v))
      .then((data) => {
        getNextJuryProjectRequest(competitionId, projectId, cmptlnglst)
        .send()
        .then((r) => {
          if (r?.status === 200) {
            return r?.json()
          }
          return r;
        })
        .then((id) => {
          dispatch(
            receiveVoteDataList({
              //@ts-ignore
              data: data.reduce((acc, a) => ([...acc, {...a }]), []),
              nextId: typeof id === 'string' ? id : ''
            })
          )
          callback && callback(true);
        })
      })
    } 
    catch(err) {
      dispatch(errorVoteDataList(String(err)));
    }
}






import { API, Competition, CompetitionAPI, Endpoints, Profile, ProjectJuryVote } from "@brg/api";
import {
  COMPETITION_LONGLIST_GET_LIST_PARAM,
  COMPETITION_LONGLIST_VOTE_CRITERIA_ID,
  normalizeJuryVoteValue,
} from "_competition/const";
import { RequestFilterT } from "shared/contexts/VoteContext";

const preserveClassicJuryVoteValue = (value: unknown): number => {
  if (value === -1 || value == null) {
    return -1;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : -1;
  }
  return -1;
};

const normalizeProjectJuryVotes = (rows: ProjectJuryVote[] | undefined): ProjectJuryVote[] =>
  (rows ?? []).map((row) => ({
    ...row,
    voteValue: row?.voteCriteriaId === COMPETITION_LONGLIST_VOTE_CRITERIA_ID
      ? normalizeJuryVoteValue(row?.voteValue)
      : preserveClassicJuryVoteValue(row?.voteValue),
  }));

export type FetchDataT = {
  competitionId: string;
  projectId: string;
  lang: string;
  profile?: Profile;
  competitionIds?: string[];
  competitionStatus?: number[];
  cmptlnglst?: boolean;
  dispatch?: any;
  callback?: (val: any) => void;
}

const getNextJuryProjectRequest = (
  competitionId: string,
  currentProjectId: string,
  cmptlnglst?: boolean,
) => {
  let request = API.request()
    .endpoint(Endpoints.competitions)
    .url(`/api/Competition/GetNextJuryProject`)
    .get()
    .addQuery('competitionId', competitionId)
    .addQuery('currentProjectId', currentProjectId);

  if (cmptlnglst) {
    request = request.addQuery(COMPETITION_LONGLIST_GET_LIST_PARAM, true);
  }

  return request;
};

export const FETCH_VOTE_DATA_LIST_REQUEST = "FETCH_VOTE_DATA_LIST_REQUEST";
export const FETCH_VOTE_DATA_LIST_SUCCESS = "FETCH_VOTE_DATA_LIST_SUCCESS";
export const FETCH_VOTE_DATA_LIST_FAILURE = "FETCH_VOTE_DATA_LIST_FAILURE";
export const RESET_FETCH_VOTE_DATA_LIST = "RESET_FETCH_VOTE_DATA_LIST";

export const UPDATE_VOTE_REQUEST = "UPDATE_VOTE_REQUEST";
export const UPDATE_VOTE_SUCCESS = "UPDATE_VOTE_SUCCESS";
export const UPDATE_VOTE_FAILURE = "UPDATE_VOTE_FAILURE";
export const RESET_UPDATE_VOTE = 'RESET_UPDATE_VOTE';

export const FETCH_COMPETITIONS_REQUEST = "FETCH_COMPETITIONS_REQUEST";
export const FETCH_COMPETITIONS_SUCCESS = "FETCH_COMPETITIONS_SUCCESS";
export const FETCH_COMPETITIONS_FAILURE = "FETCH_COMPETITIONS_FAILURE";
export const RESET_COMPETITIONS = 'RESET_COMPETITIONS';

export const GET_NEXT_REQUEST = 'GET_NEXT_REQUEST';
export const GET_NEXT_SUCCESS = 'GET_NEXT_SUCCESS';
export const GET_NEXT_FAILURE = 'GET_NEXT_FAILURE';

export const GET_NEXT_DATA_REQUEST = 'GET_NEXT_DATA_REQUEST';
export const GET_NEXT_DATA_SUCCESS = 'GET_NEXT_DATA_SUCCESS';
export const GET_NEXT_DATA_FAILURE = 'GET_NEXT_DATA_FAILURE';

export const UPDATE_REQUEST_FILTER = 'UPDATE_REQUEST_FILTER';

export const SET_CURRENT_VOTE = 'SET_CURRENT_VOTE';

export const SET_PREV_CRITERIA = 'SET_PREV_CRITERIA';

export const RESET_VOTE_DATA = 'RESET_VOTE_DATA';

export const UPDATE_VOTE_DATA = 'UPDATE_VOTE_DATA';

export const VOICE_SET = 'VOICE_SET';

export const VOTE_END = 'VOTE_END';

export const requestVoteDataList = () => {
  return {
    type: FETCH_VOTE_DATA_LIST_REQUEST,
    payload: {
      isLoading: true,
      errorMessage: "",
    },
  };
}

export const receiveVoteDataList= ({ data, nextId }: any) => {
  return {
    type: FETCH_VOTE_DATA_LIST_SUCCESS,
    payload: {
      isLoading: false,
      criteriaList: data || [],
      hasData: !!data?.length,
      ...(typeof nextId === 'string' && { nextCriteriaId: nextId })
    },
  };
}

export const errorVoteDataList = (message: string) => {
  return {
    type: FETCH_VOTE_DATA_LIST_FAILURE,
    payload: {
      isLoading: false,
      errorMessage: message,
    },
  };
}

/****************************** */

export const requestCompetitions = () => {
  return {
    type: FETCH_COMPETITIONS_REQUEST,
    payload: {
      isLoading: true,
      errorMessage: "",
    },
  };
}

export const receiveCompetitions= (data: any) => {
  return {
    type: FETCH_COMPETITIONS_SUCCESS,
    payload: {
      isLoading: false,
      competitions: data
    },
  };
}

export const errorCompetitions = (message: string) => {
  return {
    type: FETCH_COMPETITIONS_FAILURE,
    payload: {
      isLoading: false,
      errorMessage: message,
    },
  };
}

/****************************** */

export const requestUpdateVote = () => {
  return {
    type: UPDATE_VOTE_REQUEST,
    payload: {
      isLoading: true,
      errorMessage: "",
    },
  };
}

export const receiveUpdateVote= (data: any) => {
  return {
    type: UPDATE_VOTE_SUCCESS,
    payload: {
      isLoading: false,
      currentList: [...data],
    },
  };
}

export const errorUpdateVote = (message: string) => {
  return {
    type: UPDATE_VOTE_FAILURE,
    payload: {
      isLoading: false,
      errorMessage: message,
    },
  };
}

export const resetUpdateVote= () => {
  return {
    type: RESET_UPDATE_VOTE,
    payload: {
      isLoading: false,
      errorMessage: "",
      voiceSet: true
    },
  };
}

/****************************** */

export const requestGetNext = () => {
  return {
    type: GET_NEXT_REQUEST,
    payload: {
      isLoading: true,
      isNextLoading: true,
      errorMessage: "",
    },
  };
}

export const receiveGetNext= (data: any) => {
  return {
    type: GET_NEXT_SUCCESS,
    payload: {
      nextCriteriaId: data,
      isLoading: false,
      isNextLoading: false
    }
  };
}

export const errorGetNext = (message: string) => {
  return {
    type: GET_NEXT_FAILURE,
    payload: {
      isLoading: false,
      isNextLoading: false,
      errorMessage: message,
    },
  };
}

/*************************/

export const requestGetNextData = () => {
  return {
    type: GET_NEXT_DATA_REQUEST,
    payload: {
      isLoading: true,
      isNextLoading: true,
      errorMessage: "",
    },
  };
}

export const receiveGetNextData= (data: any) => {
  return {
    type: GET_NEXT_DATA_SUCCESS,
    payload: {
      isLoading: false,
      nextCriteriaList: data
    }
  };
}

export const errorGetNextData = (message: string) => {
  return {
    type: GET_NEXT_DATA_FAILURE,
    payload: {
      isLoading: false,
      errorMessage: message,
    },
  };
}

/****************************** */

export const setVoteStatus = (flag: string) => {
  return {
    type: VOTE_END,
    payload: {
      voteEnd: flag
    }
  };
}

export const updateRequestFilter= (data: RequestFilterT) => {
  return {
    type: UPDATE_REQUEST_FILTER,
    payload: data
  };
}

export const updateCurrentVote = (data: Record<string,any>[]) => {
  return {
    type: SET_CURRENT_VOTE,
    payload: {
      ...data,
      voiceSet: false
    }
  };
}

export const setPrevCrt= (id: string, prevCrt?: any) => {
  return {
    type: SET_PREV_CRITERIA,
    payload: {
      prevCriteriaId: id,
      ...(prevCrt && { prevCriteriaList: prevCrt })
    }
  };
}

export const resetVoteData = () => {
  return {
    type: RESET_VOTE_DATA,
    payload: {}
  }
}

export const updateVoteData = () => {
  return {
    type: UPDATE_VOTE_DATA,
    payload: {}
  }
}

export const resetVoteDataList = () => {
  return {
    type: RESET_FETCH_VOTE_DATA_LIST,
    payload: {
      isLoading: false,
      // criteriaList: [],
      currentList: [],
      errorMessage: "",
    },
  };
}

export const fetchNextVote = async ({ 
  competitionId, 
  projectId, 
  lang,
  dispatch, 
  callback 
}: FetchDataT) => {
  dispatch(requestGetNextData());

    try {
      await CompetitionAPI.GetProjectJuryVotes({ competitionId, projectId })
      .then((v) => v?.json())
      .then((v) => API.toLang<ProjectJuryVote[]>(v, lang))
      .then((v) => {
        //@ts-ignore
        dispatch(receiveGetNextData(v.reduce((acc, a) => ([...acc, {...a }]), [])));
        callback && callback(true);
      })
    } 
    catch(err) {
      dispatch(errorGetNextData(String(err)));
    }
}

export const fetchNextId = ({ 
  competitionId, 
  projectId,
  cmptlnglst,
  lang, 
  dispatch, 
  callback,
}: FetchDataT, updateVotedStatus?: () => void) => {
  if (competitionId && projectId) {
    dispatch(requestGetNext())
    try {
      getNextJuryProjectRequest(competitionId, projectId, cmptlnglst)
      .send()
      .then((r) => {
        if (r?.status === 200) {
          return r?.json()
        }
        return r;
      })
      .then((id) => {
        callback && callback(true)
        if (typeof id === 'string') {
          dispatch(receiveGetNext(id))
          dispatch(setPrevCrt(projectId))
          return;
        } 
        else {
          dispatch(receiveGetNext(''))
          updateVotedStatus && updateVotedStatus();
        }
      })
    }
    catch (err) {
      dispatch(errorGetNext(String(err)))
    }

  }
}

export const fetchUpdateVote = async (data: any[], dispatch: any) => {

  dispatch(requestUpdateVote());
  
  try {
    await CompetitionAPI.UpdateJuryVotes(data)
    .then((r) => {
        dispatch(receiveUpdateVote(data));
        return;
      })
  }
  catch (err) {
    dispatch(errorUpdateVote(String(err)));
  }
}


export const fetchVotes = async ({ 
  competitionId, 
  projectId,
  cmptlnglst,
  lang, 
  dispatch, 
  callback 
}: FetchDataT) => {

  if (!(competitionId && projectId)) return null;

  dispatch(requestVoteDataList());

    try {
      await CompetitionAPI.GetProjectJuryVotes({ competitionId, projectId })
      .then((v) => v?.json())
      .then((v) => API.toLang<ProjectJuryVote[]>(v, lang))
      .then((v) => normalizeProjectJuryVotes(v))
      .then((data) => {
        getNextJuryProjectRequest(competitionId, projectId, cmptlnglst)
        .send()
        .then((r) => {
          if (r?.status === 200) {
            return r?.json()
          }
          return r;
        })
        .then((id) => {
          dispatch(
            receiveVoteDataList({
              //@ts-ignore
              data: data.reduce((acc, a) => ([...acc, {...a }]), []),
              nextId: typeof id === 'string' ? id : ''
            })
          )
          callback && callback(true);
        })
      })
    } 
    catch(err) {
      dispatch(errorVoteDataList(String(err)));
    }
}






